import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const SECTIONS = [
  ["completed", "Completed", "Done and recorded.", "bg-[#EDF2EE]"],
  ["attempted", "Attempted", "Started, not finished.", "bg-[#FCFCFA]"],
  ["proposed", "Proposed · not counted", "Ideas and plans that haven't happened yet.", "bg-[#F0EBF2]"],
];
const COVER = {
  empty: "No records in this period yet. Nothing logged is not the same as zero.",
  stale: "Your last record is more than a week old, so this period says little about now.",
  sparse: "Only a few days have records, so read this lightly.",
  ok: null,
};
const fmt = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });

function List({ rows, kind }) {
  if (!rows.length) return <p className="text-sm text-[#6A646D] mt-2" data-testid={`progress-${kind}-empty`}>Nothing here for this period.</p>;
  return (
    <ul className="mt-2 space-y-2">{rows.slice(0, 30).map((r, i) => (
      <li key={`${r.date}-${i}`} className="text-sm flex gap-3" data-testid={`progress-${kind}-item-${i}`}>
        <span className="w-14 shrink-0 text-[#6A646D]">{fmt(r.date)}</span>
        <span className="text-[#302A34]">{r.text}{r.note && <span className="text-[#6A646D]"> · {r.note}</span>}</span>
      </li>))}</ul>
  );
}

export default function V2Progress() {
  const [days, setDays] = useState(7);
  const [p, setP] = useState(null);
  const [failed, setFailed] = useState(false);
  const req = useRef(0);
  const load = () => {
    const id = ++req.current;
    setP(null); setFailed(false);
    api.get(`/v2/progress?days=${days}`).then(({ data }) => { if (id === req.current) setP(data); }).catch(() => { if (id === req.current) setFailed(true); });
  };
  useEffect(load, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-6" data-testid="v2-progress">
      <div>
        <h1 className="font-display text-[28px] leading-tight text-[#302A34]">Progress</h1>
        <p className="text-sm text-[#6A646D] mt-1">Only what actually happened counts. Plans and ideas are shown separately.</p>
      </div>
      <div className="flex gap-2" role="tablist" aria-label="Period">
        {[7, 30].map((d) => (
          <button key={d} role="tab" aria-selected={days === d} onClick={() => setDays(d)} data-testid={`progress-period-${d}`}
            className={`h-11 px-4 rounded-full text-sm font-semibold chip ${days === d ? "bg-[#F0EBF2] text-[#392B43]" : "border border-[#E4E1E5] text-[#6A646D]"}`}>Last {d} days</button>
        ))}
      </div>
      {failed && <div className="card p-5" role="alert" data-testid="progress-load-error"><p className="font-semibold">Progress didn't load</p>
        <button onClick={load} data-testid="progress-retry" className="btn-ink mt-3 h-11 px-5 rounded-full text-sm font-semibold">Try again</button></div>}
      {!p && !failed && <p className="text-sm text-[#6A646D]" role="status" data-testid="progress-loading">Loading your records…</p>}
      {p && <>
        <div className="card p-5" data-testid="progress-coverage">
          <p className="text-sm text-[#302A34]">{fmt(p.period.start)} – {fmt(p.period.end)} · records on <b>{p.coverage.days_with_records} of {p.coverage.days}</b> days
            {p.coverage.last_record && <> · last record {fmt(p.coverage.last_record)}</>}</p>
          {COVER[p.coverage.status] && <p className="text-sm text-[#6A646D] mt-1" data-testid={`progress-coverage-${p.coverage.status}`}>{COVER[p.coverage.status]}</p>}
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          {SECTIONS.map(([k, title, sub, bg]) => (
            <section key={k} className={`card p-5 ${bg}`} data-testid={`progress-${k}`}>
              <div className="flex items-baseline justify-between gap-2"><h2 className="font-display text-xl">{title}</h2><span className="text-sm font-semibold" data-testid={`progress-${k}-count`}>{p.counts[k]}</span></div>
              <p className="text-xs text-[#6A646D]">{sub}</p>
              <List rows={p[k]} kind={k} />
            </section>
          ))}
        </div>
      </>}
      <Link to="/journey/progress" data-testid="progress-life-areas-link" className="inline-flex items-center h-11 text-sm underline text-[#392B43]">Life areas, trends and wins</Link>
    </div>
  );
}
